<template>
    <div class="paper-drop" :class="{ 'is-dragging': isDragging }" @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
        <input ref="fileInput" class="drop-input" type="file" :name="name" :accept="accept" multiple
            @change="onInputChange" />

        <button type="button" class="drop-target" @click="openPicker">
            <span class="drop-icon"><i :class="icon" /></span>
            <span class="drop-copy">
                <span class="drop-title">{{ title }}</span>
                <span class="drop-sub">Drop PDFs here or <em>browse</em></span>
            </span>
        </button>

        <p v-for="message in messages" :key="message" class="drop-error">
            <i class="pi pi-exclamation-circle" />{{ message }}
        </p>

        <ul v-if="files.length" class="drop-list">
            <li v-for="(file, index) in files" :key="`${file.name}-${file.size}-${index}`" class="drop-file">
                <i class="pi pi-file-pdf drop-file-icon" />
                <span class="drop-file-name" :title="file.name">{{ file.name }}</span>
                <span class="drop-file-size">{{ formatFileSize(file.size) }}</span>
                <button type="button" class="drop-file-remove" aria-label="Remove file" @click="removeFile(index)">
                    <i class="pi pi-times" />
                </button>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, defineExpose } from 'vue';
import { formatFileSize } from '@/utils/paperFiles';

const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    name: { type: String, required: true },
    title: { type: String, default: 'Add PDF files' },
    icon: { type: String, default: 'pi pi-file-pdf' },
    maxFileSize: { type: Number, required: true },
});

const emit = defineEmits(['update:modelValue']);
const accept = 'application/pdf';
const fileInput = ref(null);
const files = ref([]);
const messages = ref([]);
const isDragging = ref(false);

let dragDepth = 0;

const openPicker = () => fileInput.value?.click();

const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

const publish = () => emit('update:modelValue', files.value);

const addFiles = (incoming) => {
    const accepted = [];
    const rejected = [];

    incoming.forEach((file) => {
        if (!isPdf(file)) {
            rejected.push(`${file.name}: only PDF files can be uploaded.`);
        } else if (file.size > props.maxFileSize) {
            rejected.push(`${file.name} is ${formatFileSize(file.size)} — the limit is ${formatFileSize(props.maxFileSize)}.`);
        } else {
            accepted.push(file);
        }
    });

    messages.value = rejected;
    if (accepted.length) {
        files.value = [...files.value, ...accepted];
        publish();
    }
};

const onInputChange = (event) => {
    addFiles(Array.from(event.target.files || []));
    event.target.value = '';
};

const onDragEnter = () => {
    dragDepth += 1;
    isDragging.value = true;
};

const onDragOver = () => {
    isDragging.value = true;
};

const onDragLeave = () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) isDragging.value = false;
};

const onDrop = (event) => {
    dragDepth = 0;
    isDragging.value = false;
    addFiles(Array.from(event.dataTransfer?.files || []));
};

const removeFile = (index) => {
    files.value = files.value.filter((_, i) => i !== index);
    publish();
};

const clear = () => {
    files.value = [];
    messages.value = [];
    if (fileInput.value) fileInput.value.value = '';
};

watch(() => props.modelValue, (value) => {
    if (!value?.length && files.value.length) clear();
});

defineExpose({ clear });
</script>

<style scoped>
.paper-drop {
    --drop-accent: var(--accent-500);
    --drop-tint: var(--accent-50);
    --drop-border: var(--accent-100);
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1rem;
    border: 1px solid var(--black-50);
    border-radius: 12px;
    background-color: #fff;
    transition: border-color 0.15s ease;
}

.paper-drop.is-dragging {
    border-color: var(--drop-accent);
}

.drop-input {
    display: none;
}

.drop-target {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    padding: 1rem;
    border: 1px dashed var(--black-50);
    border-radius: 10px;
    background-color: var(--drop-tint);
    cursor: pointer;
    text-align: center;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.drop-target:hover {
    border-color: var(--drop-border);
    box-shadow: 0 2px 8px rgba(30, 40, 60, 0.06);
}

.drop-target:focus-visible {
    outline: none;
    border-color: var(--drop-border);
    box-shadow: 0 0 0 3px rgba(119, 146, 208, 0.25);
}

.drop-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 8px;
    background-color: #fff;
    color: var(--drop-accent);
    border: 1px solid var(--drop-border);
}

.drop-icon i {
    font-size: 1rem;
}

.drop-copy {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    min-width: 0;
}

.drop-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-600);
    line-height: 1.2;
}

.drop-sub {
    font-size: 0.75rem;
    color: var(--black-700);
    line-height: 1.2;
}

.drop-sub em {
    font-style: normal;
    font-weight: 500;
    color: var(--drop-accent);
    text-decoration: underline;
}

.drop-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    margin: 0;
    padding: 0;
}

.drop-file {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.5rem 0.4rem 0.6rem;
    border: 1px solid var(--black-50);
    border-radius: 8px;
    background-color: #fff;
}

.drop-file-icon {
    font-size: 0.85rem;
    color: var(--drop-accent);
    flex-shrink: 0;
}

.drop-file-name {
    flex: 1;
    min-width: 0;
    font-size: 0.8rem;
    color: var(--gray-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.drop-file-size {
    flex-shrink: 0;
    font-size: 0.7rem;
    color: var(--black-700);
}

.drop-file-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.4rem;
    height: 1.4rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--black-700);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.drop-file-remove:hover {
    background-color: #FEE2E2;
    color: #B91C1C;
}

.drop-file-remove i {
    font-size: 0.7rem;
}

.drop-error {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0;
    font-size: 0.75rem;
    color: #B91C1C;
}
</style>
